import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription, debounceTime, distinctUntilChanged, switchMap, of, catchError } from 'rxjs';
import { NgForm } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { McpConnector, McpConnectorInput, McpRegistryEntry, McpService } from '../../../services/mcp.service';
import { McpAuthPreset, PRESET_HEADER, parseMcpConfig } from './mcp-config-paste';

/**
 * Add an MCP connector, or edit its connection. Three things, nothing else:
 *
 *  * address (required); transport is detected by the backend
 *  * sign-in: a dropdown of the header shapes remote MCP servers use
 *    (Bearer · X-API-Key · Api-Key · bare Authorization · other header)
 *  * the key
 *
 * Name (empty = the server's own) and limits sit under "Advanced".
 * New connectors can start from the official MCP Registry: search, pick —
 * address, sign-in and name are filled, only the key is left.
 * Pasting a provider's Claude/Cursor snippet into the address field fills
 * address, sign-in and key from it — no separate code box.
 *
 * Saving talks to the server; on create nothing is stored if it cannot be
 * reached. Errors arrive as "MCP server: <kind> — <reason>" and are shown
 * as a translated sentence per kind, with the technical reason below.
 * The key is write-only.
 */
@Component({
  selector: 'app-mcp-connector-dialog',
  templateUrl: './mcp-connector-dialog.component.html',
  styleUrl: './mcp-connector-dialog.component.scss',
})
export class McpConnectorDialogComponent implements OnInit, OnDestroy {
  @Input() connector: McpConnector | null = null;
  @Input() onSaved: (connector: McpConnector) => void = () => {};

  readonly presets: McpAuthPreset[] = ['none', 'bearer', 'x-api-key', 'api-key', 'authorization', 'custom'];

  /** i18n keys under pages.mcp.paste.notes, after a snippet was pasted */
  pasteNotes: string[] = [];

  // ── public directory (MCP Registry) ──
  registryQuery = '';
  registryResults: McpRegistryEntry[] | null = null;
  registryLoading = false;
  /** i18n key of the failure, or null */
  registryError: string | null = null;
  /** registry cursor for the next page, null = nothing more */
  registryCursor: string | null = null;
  registryLoadingMore = false;
  /** the registry entry the form was filled from */
  picked: McpRegistryEntry | null = null;
  private registry$ = new Subject<string>();
  private registrySub?: Subscription;

  serverUrl = '';
  name = '';
  preset: McpAuthPreset = 'none';
  headerName = '';
  authSecret = '';
  clearSecret = false;
  timeoutSeconds = 15;
  maxCallsPerTurn = 3;
  maxResultChars = 8000;
  showAdvanced = false;

  saving = false;
  errorKind: string | null = null;
  errorDetail: string | null = null;

  constructor(
    protected dialogRef: NbDialogRef<McpConnectorDialogComponent>,
    private mcp: McpService,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    this.registrySub = this.registry$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((q) => {
          this.registryCursor = null;
          if (q.trim().length < 2) {
            this.registryLoading = false;
            this.registryError = null;
            return of(null);
          }
          this.registryLoading = true;
          this.registryError = null;
          return this.mcp.searchRegistry(q.trim()).pipe(catchError((err) => this.registryFailed(err)));
        })
      )
      .subscribe((page) => {
        this.registryLoading = false;
        this.registryResults = page ? this.rank(page.data) : null;
        this.registryCursor = page?.next_cursor ?? null;
      });
    const c = this.connector;
    if (!c) return;
    this.serverUrl = c.server_url;
    this.name = c.name;
    this.timeoutSeconds = c.timeout_seconds;
    this.maxCallsPerTurn = c.max_calls_per_turn;
    this.maxResultChars = c.max_result_chars;
    if (c.auth_type === 'bearer') this.preset = 'bearer';
    else if (c.auth_type === 'header') {
      const name = (c.auth_header_name || '').toLowerCase();
      const known = (Object.keys(PRESET_HEADER) as McpAuthPreset[]).find(
        (p) => PRESET_HEADER[p]!.toLowerCase() === name
      );
      this.preset = known ?? 'custom';
      if (!known) this.headerName = c.auth_header_name || '';
    }
  }

  ngOnDestroy(): void {
    this.registrySub?.unsubscribe();
  }

  onRegistryQuery(q: string): void {
    this.registryQuery = q;
    this.registry$.next(q);
  }

  /** Next page on demand; the whole list is re-ranked. */
  loadMore(): void {
    if (!this.registryCursor || this.registryLoadingMore) return;
    this.registryLoadingMore = true;
    this.registryError = null;
    this.mcp
      .searchRegistry(this.registryQuery.trim(), this.registryCursor)
      .pipe(catchError((err) => this.registryFailed(err)))
      .subscribe((page) => {
        this.registryLoadingMore = false;
        if (!page) return;
        const known = new Set((this.registryResults || []).map((e) => e.registry_name));
        const added = page.data.filter((e) => !known.has(e.registry_name));
        this.registryResults = this.rank([...(this.registryResults || []), ...added]);
        this.registryCursor = page.next_cursor;
      });
  }

  private rank(rows: McpRegistryEntry[]): McpRegistryEntry[] {
    return [...rows].sort((a, b) => (b.relevance ?? 0) - (a.relevance ?? 0));
  }

  private registryFailed(err: any) {
    this.registryLoadingMore = false;
    this.registryError =
      err?.error?.detail?.code === 'registry_slow' || err?.status === 504
        ? 'pages.mcp.registry.slow'
        : 'pages.mcp.registry.failed';
    return of(null);
  }

  /** Fill the form from a registry entry; only the key is left to enter. */
  pick(entry: McpRegistryEntry): void {
    this.picked = entry;
    this.serverUrl = entry.url;
    this.name = entry.title;
    this.preset = entry.auth.known ? entry.auth.preset : 'none';
    this.headerName = entry.auth.header_name ?? '';
    this.pasteNotes = [];
    this.errorKind = this.errorDetail = null;
    this.registryResults = null;
    this.registryCursor = null;
    this.registryQuery = '';
  }

  trackEntry = (_: number, e: McpRegistryEntry) => e.registry_name;

  get isEdit(): boolean {
    return !!this.connector;
  }

  get hasStoredSecret(): boolean {
    return !!this.connector?.has_auth_secret && !this.clearSecret;
  }

  /** A key is required whenever sign-in is not "none" — unless one is stored. */
  get secretMissing(): boolean {
    return this.preset !== 'none' && !this.authSecret.trim() && !this.hasStoredSecret;
  }

  /** The header the chosen preset sends, for the "sent as" line. */
  get sentHeader(): string {
    return this.preset === 'custom' ? this.headerName.trim() || '…' : PRESET_HEADER[this.preset] ?? '';
  }

  /** A provider snippet pasted into the address field: take what it holds. */
  onUrlPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text') ?? '';
    if (!text.trim().startsWith('{')) return;
    const parsed = parseMcpConfig(text);
    if (!parsed) return;
    event.preventDefault();
    this.pasteNotes = parsed.notes;
    if (parsed.url) this.serverUrl = parsed.url;
    if (parsed.name && !this.name) this.name = parsed.name;
    this.preset = parsed.preset;
    this.headerName = parsed.headerName ?? '';
    if (parsed.secret) this.authSecret = parsed.secret;
    this.errorKind = this.errorDetail = null;
  }

  submit(form: NgForm): void {
    this.errorKind = this.errorDetail = null;
    if (form.invalid || this.secretMissing) {
      form.form.markAllAsTouched();
      return;
    }
    const input: McpConnectorInput = {
      server_url: this.serverUrl.trim(),
      auth_type: this.preset === 'none' ? 'none' : this.preset === 'bearer' ? 'bearer' : 'header',
      auth_header_name:
        this.preset === 'custom' ? this.headerName.trim() : PRESET_HEADER[this.preset] ?? null,
      timeout_seconds: this.timeoutSeconds,
      max_calls_per_turn: this.maxCallsPerTurn,
      max_result_chars: this.maxResultChars,
    };
    const name = this.name.trim();
    if (name) input.name = name;
    if (this.preset !== 'none' && this.authSecret.trim()) input.auth_secret = this.authSecret.trim();
    if (this.isEdit && (this.clearSecret || this.preset === 'none') && this.connector?.has_auth_secret && !input.auth_secret) {
      input.clear_auth_secret = true;
    }

    this.saving = true;
    const req$ = this.isEdit ? this.mcp.update(this.connector!.id, input) : this.mcp.create(input);
    req$.subscribe({
      next: (saved) => {
        this.saving = false;
        this.authSecret = '';
        this.onSaved(saved);
        this.dialogRef.close();
      },
      error: (err) => {
        this.saving = false;
        const detail = typeof err?.error?.detail === 'string' ? err.error.detail : '';
        const m = /^MCP server: (\w+) — (.*)$/.exec(detail);
        if (m) {
          this.errorKind = m[1];
          this.errorDetail = m[2];
        } else {
          this.errorKind = 'other';
          this.errorDetail = detail || this.t.instant('pages.mcp.errors.saveFailed');
        }
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
